import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/authContext.ts";
import { apiConfigurada, contarPendientes } from "../../lib/api.ts";
import { useHashSection } from "../../lib/hashRoute.ts";
import { C, SANS, SERIF } from "../../ui/theme.ts";
import {
  IconoCaptacion,
  IconoCumplimiento,
  IconoEdificio,
  IconoEngrane,
  IconoLlave,
  IconoOjo,
  IconoPersonas,
  IconoProyecto,
  IconoReporte,
  LogoArgus,
} from "../../ui/iconos.tsx";
import { PersonasView } from "./PersonasView.tsx";
import { CaptacionView } from "./CaptacionView.tsx";
import { ReportesView } from "./ReportesView.tsx";
import { CompraventasView } from "./CompraventasView.tsx";
import { ArrendamientosView } from "./ArrendamientosView.tsx";
import { ProyectosView } from "./ProyectosView.tsx";
import { PendientesView } from "./PendientesView.tsx";
import { CumplimientoView } from "./CumplimientoView.tsx";
import { ConfiguracionView } from "./ConfiguracionView.tsx";

/**
 * Plano de CUMPLIMIENTO — layout maestro-detalle de 3 columnas (IA fijada por
 * Bernardo, mockup v3): rail de secciones · lista central · detalle/captura.
 * Rail con la identidad de marca (issue #47/#50): logo del ojo + wordmark
 * serif sobre tiel fuerte, iconografía SVG propia en lugar de emojis.
 */

const SECCIONES = [
  "pendientes",
  "captacion",
  "compraventas",
  "arrendamientos",
  "personas",
  "proyectos",
  "reportes",
  "cumplimiento",
  "config",
] as const;

type Seccion = typeof SECCIONES[number];

const PESO_ACTIVO = 600;
const PESO_INACTIVO = 500;
const OPACIDAD_ICONO_INACTIVO = 0.85;
const MAX_BADGE_PENDIENTES = 99;

const NAV: { id: Seccion; icono: ReactNode; etiqueta: string }[] = [
  { id: "pendientes", icono: <IconoOjo />, etiqueta: "Pendientes" },
  { id: "captacion", icono: <IconoCaptacion />, etiqueta: "Captación" },
  { id: "compraventas", icono: <IconoEdificio />, etiqueta: "Compraventas" },
  { id: "arrendamientos", icono: <IconoLlave />, etiqueta: "Arrendamientos" },
  { id: "personas", icono: <IconoPersonas />, etiqueta: "Personas" },
  { id: "proyectos", icono: <IconoProyecto />, etiqueta: "Proyectos" },
  { id: "reportes", icono: <IconoReporte />, etiqueta: "Reportes" },
  { id: "cumplimiento", icono: <IconoCumplimiento />, etiqueta: "Cumplimiento" },
  { id: "config", icono: <IconoEngrane />, etiqueta: "Configuración" },
];

export function RecApp() {
  const { session, setSession } = useAuth();
  const token = session?.accessToken ?? "";
  const demo = !apiConfigurada() || token === "";
  const [seccion, setSeccion] = useHashSection("rec", SECCIONES, "personas");
  const [pendientes, setPendientes] = useState<number | null>(null);

  useEffect(() => {
    if (demo) {
      setPendientes(null);
      return;
    }
    let cancelado = false;
    contarPendientes(token)
      .then((n) => {
        if (!cancelado) setPendientes(n);
      })
      .catch(() => {
        if (!cancelado) setPendientes(null);
      });
    return () => {
      cancelado = true;
    };
  }, [demo, token, seccion]);

  return (
    <div className="argus-rec-shell" style={{ display: "flex", height: "100vh", fontFamily: SANS, background: C.bg, color: C.ink }}>
      {/* Rail de secciones — identidad de marca sobre tiel fuerte */}
      <aside
        className="argus-rec-rail"
        style={{
          width: 172,
          background: C.rail,
          display: "flex",
          flexDirection: "column",
          padding: "16px 10px 12px",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <div className="argus-rec-brand" style={{ display: "flex", alignItems: "center", gap: 9, padding: "0 8px 16px" }}>
          <LogoArgus alto={22} color="#F3EFE3" colorPupila={C.oro} />
          <span style={{ fontFamily: SERIF, fontSize: 15, letterSpacing: "0.26em", color: "#F3EFE3", fontWeight: 500 }}>
            ARGUS
          </span>
        </div>
        {NAV.map((n) => {
          const activo = seccion === n.id;
          return (
            <button
              className="argus-rec-nav-btn"
              key={n.id}
              onClick={() => { setSeccion(n.id); }}
              style={{
                width: "100%",
                border: "none",
                background: activo ? C.tiel : "transparent",
                color: activo ? "#FFFFFF" : "#B9CEC9",
                borderRadius: 8,
                padding: "8px 10px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 12.5,
                fontWeight: activo ? PESO_ACTIVO : PESO_INACTIVO,
                textAlign: "left",
                position: "relative",
              }}
            >
              {activo && (
                <span
                  className="argus-rec-nav-indicator"
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: -10,
                    top: 8,
                    bottom: 8,
                    width: 3,
                    borderRadius: 2,
                    background: C.oro,
                  }}
                />
              )}
              <span style={{ display: "inline-flex", flexShrink: 0, opacity: activo ? 1 : OPACIDAD_ICONO_INACTIVO }}>{n.icono}</span>
              {n.etiqueta}
              {n.id === "pendientes" && pendientes !== null && pendientes > 0 && (
                <span
                  aria-label={`${String(pendientes)} pendientes`}
                  style={{
                    position: "absolute",
                    top: 5,
                    right: 7,
                    minWidth: 18,
                    height: 18,
                    borderRadius: 999,
                    background: C.oro,
                    color: C.tielDark,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10.5,
                    fontWeight: 800,
                    padding: "0 5px",
                  }}
                >
                  {pendientes > MAX_BADGE_PENDIENTES ? `${String(MAX_BADGE_PENDIENTES)}+` : pendientes}
                </span>
              )}
            </button>
          );
        })}
        <div className="argus-rec-session" style={{ marginTop: "auto", borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: 10 }}>
          <div style={{ padding: "0 8px", fontSize: 11, color: "#8FAAA4", lineHeight: 1.45 }}>
            <span style={{ display: "block", color: "#D8E6E2", fontWeight: 600, fontSize: 11.5 }}>
              {session?.nombre}
            </span>
            {session?.rol}
          </div>
          <button
            onClick={() => { setSession(null); }}
            style={{
              margin: "8px 8px 0",
              border: "1px solid rgba(255,255,255,0.25)",
              background: "transparent",
              color: "#D8E6E2",
              fontSize: 11.5,
              borderRadius: 7,
              padding: "4px 12px",
              cursor: "pointer",
            }}
          >
            Salir
          </button>
        </div>
      </aside>

      {/* Contenido de la sección */}
      {seccion === "personas" ? (
        <PersonasView />
      ) : seccion === "captacion" ? (
        <CaptacionView />
      ) : seccion === "reportes" ? (
        <ReportesView />
      ) : seccion === "compraventas" ? (
        <CompraventasView />
      ) : seccion === "arrendamientos" ? (
        <ArrendamientosView />
      ) : seccion === "proyectos" ? (
        <ProyectosView />
      ) : seccion === "pendientes" ? (
        <PendientesView irA={(s) => { setSeccion(s as Seccion); }} />
      ) : seccion === "cumplimiento" ? (
        <CumplimientoView />
      ) : (
        <ConfiguracionView />
      )}
    </div>
  );
}
